// Thin façade over sdk-core 14 for the escrow-nft (NFT-Liquidity) contract.
// Mirror of escrow.ts in spirit: queries return plain JS values, write
// builders return UNSIGNED transactions (the connected DappProvider signs
// them via auth.ts and we broadcast via ApiNetworkProvider).

import {
  Address,
  AbiRegistry,
  ApiNetworkProvider,
  SmartContractController,
  SmartContractTransactionsFactory,
  TransactionsFactoryConfig,
  Token,
  TokenTransfer,
  type Transaction,
} from "@multiversx/sdk-core";

import escrowNftAbiJson from "./abi/escrow_nft.abi.json";
import { NETWORK_URLS, type AppConfig } from "./config";
import { getProvider } from "./auth";

const CHAIN_IDS: Record<string, string> = {
  mainnet: "1",
  testnet: "T",
  devnet:  "D",
};

const GAS_CALL = 50_000_000n;

let _api: ApiNetworkProvider | null = null;
let _ctrl: SmartContractController | null = null;
let _factory: SmartContractTransactionsFactory | null = null;
let _abi: AbiRegistry | null = null;

function abi(): AbiRegistry {
  if (!_abi) _abi = AbiRegistry.create(escrowNftAbiJson as any);
  return _abi;
}

function api(cfg: AppConfig): ApiNetworkProvider {
  if (!_api) _api = new ApiNetworkProvider(NETWORK_URLS[cfg.network].api);
  return _api;
}

function ctrl(cfg: AppConfig): SmartContractController {
  if (!_ctrl) {
    _ctrl = new SmartContractController({
      chainID: CHAIN_IDS[cfg.network],
      networkProvider: api(cfg),
      abi: abi(),
    });
  }
  return _ctrl;
}

function factory(cfg: AppConfig): SmartContractTransactionsFactory {
  if (!_factory) {
    const config = new TransactionsFactoryConfig({ chainID: CHAIN_IDS[cfg.network] });
    _factory = new SmartContractTransactionsFactory({ config, abi: abi() });
  }
  return _factory;
}

const addr = (s: string) => Address.newFromBech32(s);
const bech = (a: unknown): string =>
  typeof a === "string" ? a : (a as any)?.toBech32 ? (a as any).toBech32() : String(a);

function bn(v: any): bigint {
  if (v == null) return 0n;
  if (typeof v === "bigint") return v;
  if (typeof v === "number") return BigInt(Math.trunc(v));
  if (typeof v.toFixed === "function") return BigInt(v.toFixed(0));
  const s = String(v);
  if (/^-?\d+$/.test(s)) return BigInt(s);
  return BigInt(Math.trunc(Number(s)));
}

function sc(cfg: AppConfig): string {
  if (!cfg.nftLiquidityContractAddress) {
    throw new Error("nftLiquidityContractAddress not configured");
  }
  return cfg.nftLiquidityContractAddress;
}

// ─── reads ───────────────────────────────────────────────────────────
async function q(cfg: AppConfig, fn: string, args: any[] = []): Promise<any[]> {
  return await ctrl(cfg).query({
    contract: addr(sc(cfg)),
    function: fn,
    arguments: args,
  });
}

export type NftConfig = {
  paused: boolean;
  externalPaused: boolean;
  minDepositWegld: bigint;
  noSwapWithdrawFee: bigint;
  profitWallet: string;
  wegldTokenId: string;
  acceptedNftCollection: string;
};

export async function getNftConfig(cfg: AppConfig): Promise<NftConfig> {
  const r = await q(cfg, "getConfig");
  const x = r[0];
  return {
    paused:                !!x.paused,
    externalPaused:        !!x.external_paused,
    minDepositWegld:       bn(x.min_deposit_wegld),
    noSwapWithdrawFee:     bn(x.no_swap_withdraw_fee),
    profitWallet:          bech(x.profit_wallet),
    wegldTokenId:          String(x.wegld_token_id),
    acceptedNftCollection: String(x.accepted_nft_collection),
  };
}

export type NftPool = {
  exists: boolean;
  creator: string;
  wegld: bigint;
  spreadBps: number;
  platformFeeBps: number;
  swapsServed: number;
  /** True once the no-swap withdraw fee has been charged. Once true (or
   *  swaps_served > 0), subsequent withdraws are free. */
  noSwapFeePaid: boolean;
};

function parsePool(p: any): NftPool {
  return {
    exists:         !!p.exists,
    creator:        bech(p.creator),
    wegld:          bn(p.wegld),
    spreadBps:      Number(p.spread_bps),
    platformFeeBps: Number(p.platform_fee_bps),
    swapsServed:    Number(p.swaps_served),
    noSwapFeePaid:  !!p.no_swap_fee_paid,
  };
}

export async function getNftPool(cfg: AppConfig, creator: string): Promise<NftPool> {
  const r = await q(cfg, "getPool", [addr(creator)]);
  return parsePool(r[0]);
}

// sdk-core wraps variadic returns in a nested array; same trick as in
// escrow.ts unwrapVariadic for getPoolCreators. Empty list comes back as
// `[[]]`, non-empty as `[[struct, struct, ...]]`.
function unwrapVariadic(r: any): unknown[] {
  if (!Array.isArray(r)) return [];
  if (r.length === 1 && Array.isArray(r[0])) return r[0];
  return r;
}

export async function getAllNftPools(cfg: AppConfig): Promise<NftPool[]> {
  const r = await q(cfg, "getAllPools");
  return unwrapVariadic(r)
    .map((p) => parsePool(p))
    .filter((p) => p.exists);
}

export type NftQuote = {
  faceValue:          bigint;
  spreadBps:          number;
  platformFeeBps:     number;
  normalPayout:       bigint;
  platformFee:        bigint;
  maxPayout:          bigint;
  poolWegldAvailable: bigint;
  fullyCovered:       boolean;
};

export async function getNftQuote(
  cfg: AppConfig,
  poolCreator: string,
  faceValue: bigint,
): Promise<NftQuote> {
  const r = await q(cfg, "getQuote", [addr(poolCreator), faceValue]);
  const x = r[0];
  return {
    faceValue:          bn(x.face_value),
    spreadBps:          Number(x.spread_bps),
    platformFeeBps:     Number(x.platform_fee_bps),
    normalPayout:       bn(x.normal_payout),
    platformFee:        bn(x.platform_fee),
    maxPayout:          bn(x.max_payout),
    poolWegldAvailable: bn(x.pool_wegld_available),
    fullyCovered:       !!x.fully_covered,
  };
}

/** Decode an HLSR-style NFT's face value via the on-chain parser.
 *  `holder` must actually own the NFT — otherwise the contract panics
 *  with "input too short" (get_esdt_token_data returns empty for
 *  non-holders). For dApp purposes, pass the connected user's address. */
export async function getHlsrFaceValue(
  cfg: AppConfig,
  holder: string,
  nonce: number,
): Promise<bigint> {
  const r = await q(cfg, "getHlsrFaceValue", [addr(holder), nonce]);
  return bn(r[0]);
}

// ─── writes — unsigned tx builders ───────────────────────────────────
async function fetchNonce(cfg: AppConfig, address: string): Promise<bigint> {
  const acc = await api(cfg).getAccount(addr(address));
  return BigInt(acc.nonce);
}

async function withNonce(cfg: AppConfig, sender: string, tx: Transaction): Promise<Transaction> {
  tx.nonce = await fetchNonce(cfg, sender);
  return tx;
}

export async function buildCreateNftPool(
  cfg: AppConfig,
  sender: string,
  wegldTokenId: string,
  initialAmount: bigint,
  spreadBps: number,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "createPool",
    gasLimit: GAS_CALL,
    arguments: [spreadBps],
    tokenTransfers: [
      new TokenTransfer({
        token: new Token({ identifier: wegldTokenId }),
        amount: initialAmount,
      }),
    ],
  }));
}

export async function buildFundNftPool(
  cfg: AppConfig,
  sender: string,
  wegldTokenId: string,
  amount: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "fundPool",
    gasLimit: GAS_CALL,
    arguments: [],
    tokenTransfers: [
      new TokenTransfer({
        token: new Token({ identifier: wegldTokenId }),
        amount,
      }),
    ],
  }));
}

export async function buildWithdrawNftPool(
  cfg: AppConfig,
  sender: string,
  amount: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "withdrawFromPool",
    gasLimit: GAS_CALL,
    arguments: [amount],
  }));
}

export async function buildCloseNftPool(
  cfg: AppConfig,
  sender: string,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "closePool",
    gasLimit: GAS_CALL,
    arguments: [],
  }));
}

export async function buildNftSwap(
  cfg: AppConfig,
  sender: string,
  poolCreator: string,
  nftTokenId: string,
  nftNonce: number,
  minPayout: bigint,
): Promise<Transaction> {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "swap",
    gasLimit: GAS_CALL,
    arguments: [addr(poolCreator), minPayout],
    tokenTransfers: [
      new TokenTransfer({
        token: new Token({ identifier: nftTokenId, nonce: BigInt(nftNonce) }),
        amount: 1n,
      }),
    ],
  }));
}

// admin
export async function buildNftPause(cfg: AppConfig, sender: string, paused: boolean) {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "pause",
    gasLimit: GAS_CALL,
    arguments: [paused],
  }));
}

export async function buildNftPauseExternal(cfg: AppConfig, sender: string, value: boolean) {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "pauseExternal",
    gasLimit: GAS_CALL,
    arguments: [value],
  }));
}

export async function buildNftSetMinDeposit(cfg: AppConfig, sender: string, amount: bigint) {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "setMinDeposit",
    gasLimit: GAS_CALL,
    arguments: [amount],
  }));
}

export async function buildNftSetNoSwapFee(cfg: AppConfig, sender: string, amount: bigint) {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "setNoSwapWithdrawFee",
    gasLimit: GAS_CALL,
    arguments: [amount],
  }));
}

export async function buildNftSetProfitWallet(cfg: AppConfig, sender: string, walletAddr: string) {
  return withNonce(cfg, sender, factory(cfg).createTransactionForExecute(addr(sender), {
    contract: addr(sc(cfg)),
    function: "setProfitWallet",
    gasLimit: GAS_CALL,
    arguments: [addr(walletAddr)],
  }));
}

// ─── sign + broadcast ────────────────────────────────────────────────
export async function signAndSendNft(cfg: AppConfig, tx: Transaction): Promise<string> {
  const provider = getProvider();
  if (!provider) throw new Error("wallet not connected");
  const [signed] = await provider.signTransactions([tx]);
  const hash = await api(cfg).sendTransaction(signed);
  return hash;
}

export async function awaitCompletedNft(cfg: AppConfig, hash: string) {
  return await api(cfg).awaitTransactionCompleted(hash);
}

// ─── user NFT discovery (off-chain, via API) ─────────────────────────
export type UserHlsrNft = {
  nonce: number;
  identifier: string;
  /** Decoded face value in raw integer EGLD (18 decimals). */
  faceValue: bigint;
  requestEpoch: number;
  claimEpoch: number;
};

/** Discover an address' HLSR NFTs and decode each one's attributes off-chain
 *  using the same layout the on-chain parser uses. Used to populate the
 *  swap-tab dropdown without having to call the SC view for each. */
export async function getUserHlsrNfts(
  cfg: AppConfig,
  address: string,
  collection: string,
): Promise<UserHlsrNft[]> {
  if (!address || !collection) return [];
  const url = `${NETWORK_URLS[cfg.network].api}/accounts/${address}/nfts?collections=${collection}&size=200`;
  const resp = await fetch(url);
  if (!resp.ok) return [];
  const arr = (await resp.json()) as Array<{ identifier: string; nonce: number; attributes?: string }>;
  return arr
    .map((n) => decodeHlsrAttributes(n))
    .filter((x): x is UserHlsrNft => x !== null);
}

function decodeHlsrAttributes(n: {
  identifier: string;
  nonce: number;
  attributes?: string;
}): UserHlsrNft | null {
  if (!n.attributes) return null;
  try {
    const raw = atobToBytes(n.attributes);
    // Layout: [32 zero bytes][4-byte BE len][BigUint payload]×2 [u64 BE]×2
    if (raw.length < 32 + 4 + 1 + 4 + 1 + 8 + 8) return null;
    let i = 32;
    const len1 = readU32BE(raw, i); i += 4;
    const face = readBigUintBE(raw, i, len1); i += len1;
    const len2 = readU32BE(raw, i); i += 4;
    /* hsegldBurned */ readBigUintBE(raw, i, len2); i += len2;
    const reqEpoch = Number(readU64BE(raw, i)); i += 8;
    const claimEpoch = Number(readU64BE(raw, i)); i += 8;
    return {
      identifier: n.identifier,
      nonce: n.nonce,
      faceValue: face,
      requestEpoch: reqEpoch,
      claimEpoch,
    };
  } catch {
    return null;
  }
}

function atobToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function readU32BE(buf: Uint8Array, off: number): number {
  return (
    (buf[off] << 24) |
    (buf[off + 1] << 16) |
    (buf[off + 2] << 8) |
    buf[off + 3]
  ) >>> 0;
}

function readU64BE(buf: Uint8Array, off: number): bigint {
  let v = 0n;
  for (let i = 0; i < 8; i++) v = (v << 8n) | BigInt(buf[off + i]);
  return v;
}

function readBigUintBE(buf: Uint8Array, off: number, len: number): bigint {
  let v = 0n;
  for (let i = 0; i < len; i++) v = (v << 8n) | BigInt(buf[off + i]);
  return v;
}

// ─── admin API (server-side state, gated by sign-message) ────────────
// On first admin call, we ask the connected wallet to sign a canonical
// message containing a timestamp. The signature is cached in
// sessionStorage for up to 50 minutes (the server accepts signatures
// up to 1h old; 50 min leaves a safety margin). When expired/missing,
// we silently re-prompt the wallet for a fresh signature.

import { Message } from "@multiversx/sdk-core";

const ADMIN_SIG_KEY = "escrow_admin_sig_v2";
const SIG_TTL_MS    = 50 * 60 * 1000; // resign every 50 min

type CachedSig = {
  addr: string;
  ts:   string;
  sig:  string;
};

function readCachedSig(): CachedSig | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(ADMIN_SIG_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) as CachedSig;
    if (Date.now() - new Date(obj.ts).getTime() > SIG_TTL_MS) return null;
    return obj;
  } catch {
    return null;
  }
}

function writeCachedSig(c: CachedSig): void {
  sessionStorage.setItem(ADMIN_SIG_KEY, JSON.stringify(c));
}

function clearCachedSig(): void {
  sessionStorage.removeItem(ADMIN_SIG_KEY);
}

function buildAdminMessageText(timestampIso: string): string {
  return `escrow.djooga.com admin login at ${timestampIso}`;
}

async function getOrRequestAdminSig(address: string): Promise<CachedSig> {
  const cached = readCachedSig();
  if (cached && cached.addr.toLowerCase() === address.toLowerCase()) return cached;

  const provider = getProvider();
  if (!provider) throw new Error("connect a wallet first");

  const ts = new Date().toISOString();
  const messageText = buildAdminMessageText(ts);
  const message = new Message({
    data: new TextEncoder().encode(messageText),
    address: Address.newFromBech32(address),
  });

  // sdk-dapp providers all expose signMessage. xPortal/Ledger/Web Wallet
  // open their own UI; the extension shows the message in its popup.
  const signed = await provider.signMessage(message);
  if (!signed || !signed.signature) throw new Error("message signing cancelled");

  const sigHex = bytesToHex(signed.signature as Uint8Array);
  const entry  = { addr: address, ts, sig: sigHex };
  writeCachedSig(entry);
  return entry;
}

function bytesToHex(b: Uint8Array): string {
  let s = "";
  for (let i = 0; i < b.length; i++) {
    s += b[i].toString(16).padStart(2, "0");
  }
  return s;
}

/** Caller passes the currently-connected wallet address. The helper
 *  obtains (or reuses) a signed admin token. */
let _currentAddress = "";
export function setAdminAddress(addr: string) {
  _currentAddress = addr;
}

async function adminFetch(path: string, init: RequestInit = {}): Promise<any> {
  if (!_currentAddress) {
    throw new Error("admin fetch attempted before setAdminAddress was called");
  }
  const sig = await getOrRequestAdminSig(_currentAddress);
  const headers = {
    ...(init.headers || {}),
    "Content-Type": "application/json",
    "x-admin-address":   sig.addr,
    "x-admin-timestamp": sig.ts,
    "x-admin-signature": sig.sig,
  };
  const resp = await fetch(path, { ...init, headers });
  if (resp.status === 401) {
    clearCachedSig();
    throw new Error("Admin signature rejected — please retry (the wallet will be asked again).");
  }
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${await resp.text()}`);
  }
  return await resp.json();
}

export type BotStatus = {
  address: string;
  network: string;
  paused: boolean;
  interval_minutes: number;
  min_nft_age_seconds: number;
  message: string;
  last_processed_nonce: number;
  last_processed_at: string | null;
  started_at: string | null;
  txs_sent_total: number;
  txs_skipped_blacklist: number;
  txs_skipped_sc: number;
  txs_skipped_age: number;
  txs_skipped_already_processed: number;
  last_error: string | null;
};

export async function getBotStatus(): Promise<BotStatus> {
  return await adminFetch("/api/admin/bot/state");
}

export async function updateBotState(patch: Partial<BotStatus>): Promise<BotStatus> {
  return await adminFetch("/api/admin/bot/state", {
    method: "POST",
    body: JSON.stringify(patch),
  });
}

export async function botTickNow(): Promise<any> {
  return await adminFetch("/api/admin/bot/tick-now", { method: "POST" });
}

export type BlacklistEntry = {
  address: string;
  reason: string;
  added_at: string;
};

export async function getBlacklist(): Promise<BlacklistEntry[]> {
  const r = await adminFetch("/api/admin/blacklist");
  return r.entries ?? [];
}

export async function blacklistAdd(address: string, reason: string): Promise<void> {
  await adminFetch("/api/admin/blacklist/add", {
    method: "POST",
    body: JSON.stringify({ address, reason }),
  });
}

export async function blacklistRemove(address: string): Promise<void> {
  await adminFetch("/api/admin/blacklist/remove", {
    method: "POST",
    body: JSON.stringify({ address }),
  });
}

export async function getGasConfig(): Promise<Record<string, number>> {
  const resp = await fetch("/api/gas-config");
  if (!resp.ok) return {};
  return await resp.json();
}

export async function updateGasConfig(patch: Record<string, number>): Promise<Record<string, number>> {
  return await adminFetch("/api/admin/gas-config", {
    method: "POST",
    body: JSON.stringify(patch),
  });
}

// ─── ABI sync — Vite copies abi from contract-nft/output at build time ───
// (see dapp/package.json prebuild script)
